import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Generic CTA section configurable per-page (label + handler).
 * Replaces direct Audit-only CTAs so /websites can say "Start My Website" and
 * /revenue-systems can say "Book Revenue Leak Audit".
 */
export default function FinalCta({
  eyebrow = "The Next Step",
  headline,
  italicTail,
  body,
  primaryLabel = "Book My Revenue Audit",
  secondaryLabel,
  secondaryHref,
  onPrimary,
}) {
  const handlePrimary = () => {
    if (onPrimary) onPrimary();
    else window.dispatchEvent(new Event("open-audit"));
  };

  return (
    <section data-testid="final-cta-section" className="px-5 sm:px-6 md:px-10 pb-12">
      <div className="max-w-7xl mx-auto">
        <div className="relative rounded-[24px] sm:rounded-[28px] bg-ink-deep text-sand-100 overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(700px 360px at 12% 20%, rgba(165,106,67,0.35), transparent 60%), radial-gradient(700px 360px at 90% 80%, rgba(111,115,94,0.18), transparent 60%)",
            }}
          />
          <div className="relative px-6 sm:px-8 md:px-14 py-16 sm:py-20 md:py-28 lg:py-32 grid lg:grid-cols-12 gap-8 md:gap-10 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow !text-sand-300">{eyebrow}</p>
              <h2 className="font-display mt-4 sm:mt-5 text-[2.25rem] sm:text-4xl md:text-5xl lg:text-6xl xl:text-[4.4rem] leading-[1.05] sm:leading-[1] tracking-[-0.02em] font-semibold">
                {headline}
                {italicTail && (
                  <>
                    <br />
                    <span className="italic text-sand-300 font-normal">
                      {italicTail}
                    </span>
                  </>
                )}
              </h2>
              {body && (
                <p className="mt-5 sm:mt-7 max-w-xl text-[15px] sm:text-base md:text-lg text-sand-300 leading-relaxed">
                  {body}
                </p>
              )}
            </div>
            <div className="lg:col-span-4 reveal flex flex-col gap-3 lg:items-end">
              <button
                type="button"
                onClick={handlePrimary}
                data-testid="final-cta-primary"
                className="w-full lg:w-auto group inline-flex items-center justify-center gap-2 rounded-full bg-sand-100 text-ink-deep px-7 py-4 text-[15px] font-medium hover:bg-sand-50 transition-colors min-h-[52px]"
              >
                {primaryLabel}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </button>
              {secondaryLabel && secondaryHref && (
                secondaryHref.startsWith("/") && !secondaryHref.startsWith("//") ? (
                  <Link
                    to={secondaryHref}
                    data-testid="final-cta-secondary"
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-sand-300/30 text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-white/5 transition-colors min-h-[52px]"
                  >
                    {secondaryLabel}
                  </Link>
                ) : (
                  <a
                    href={secondaryHref}
                    data-testid="final-cta-secondary"
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-sand-300/30 text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-white/5 transition-colors min-h-[52px]"
                  >
                    {secondaryLabel}
                  </a>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
